import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { isNativeApp } from '../utils/platform';

// Poll interval for checking whether a new deployment is live.
const POLL_MS = 60000;

/**
 * Reads the CRA build fingerprint (asset-manifest.json, which contains the
 * content-hashed bundle filenames). When the deployed bundle changes, this
 * value changes — that's our signal that a new version was deployed.
 */
const fetchBuildId = async () => {
  try {
    const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return JSON.stringify(data.files || data);
  } catch (e) {
    return null;
  }
};

/**
 * "Update available — tap to refresh" banner for the native app (Live Web).
 * Detects new deployments in the background and lets the user reload to get
 * the latest UI without reinstalling. Native-only; no-ops on the web build.
 */
const UpdateAvailableBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialRef = useRef(null);

  useEffect(() => {
    if (!isNativeApp()) return undefined;

    let cancelled = false;
    let interval;

    const check = async () => {
      if (cancelled || updateAvailable) return;
      const current = await fetchBuildId();
      if (!current) return;
      if (initialRef.current == null) {
        initialRef.current = current; // first successful read = baseline
        return;
      }
      if (current !== initialRef.current) {
        setUpdateAvailable(true);
        if (interval) clearInterval(interval);
      }
    };

    check(); // establish baseline ASAP
    interval = setInterval(check, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!updateAvailable) return null;

  return (
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold py-2.5 px-5 rounded-full shadow-xl active:scale-95 transition"
      data-testid="update-available-banner"
    >
      <RefreshCw className="w-4 h-4" />
      Update available — tap to refresh
    </button>
  );
};

export default UpdateAvailableBanner;
